import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, Alert, List, Icon } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, CTA } from '../../components';
import ImageBanner from '../../components/common/image-banner';
import OrderForm from '../../components/template/brochures/order-form';
import BrochureCard from '../../components/template/brochures/brochure-card';
import CardSection from '../../components/common/card-section';
import HeroSimple from '../../components/common/hero-simple';
import styles from './index.module.scss';
import { isValidArray } from '../../components/utilities/validations';

const Brochures = (props) => {
	const pageData = props.data.contentstackBrochures;
	const [brochures, setBrochures] = useState({});
	const [showForm, setShowForm] = useState(false);
	const [showMenu, setShowMenu] = useState(false);
	const [success, setSuccess] = useState('');

	const updateBrochureSelection = (item) => {
		if (Object.keys(brochures).length > 0) {
			setBrochures({});
		} else {
			setBrochures(item);
		}
	};

	return (
		<Layout
			id='template-Brochures'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && <HeroSimple data={pageData.hero} />}

			{pageData.top_content && (
				<div className={styles.broIntro}>
					<Container width={1200}>
						{pageData.top_content.heading && <h2 className={`${styles.sectionTitle} s2-h2__alt`}>{pageData.top_content.heading}</h2>}
						{pageData.top_content.subheading && <p>{pageData.top_content.subheading}</p>}

						{pageData.top_content.ctas?.[0] && (
							<div className={styles.anchorLinks}>
								<button
									onClick={() => setShowMenu(!showMenu)}
									className={styles.anchorLinks_MobileButton}
								>
									In This Section
									<Icon name='arrow_drop_down' />
								</button>

								<List
									bullets='none'
									className={`${styles.anchorLinks_List} ${showMenu ? styles.anchorLinks_List_active : ''}`}
								>
									{pageData.top_content.ctas.map((link, index) => (
										<li key={index}>
											<CTA data={link} />
										</li>
									))}
								</List>
							</div>
						)}
					</Container>
				</div>
			)}

			{success && (
				<Container
					width={1800}
					className={styles.broContainer}
				>
					<Alert
						type='success'
						className='mb-4'
						hideClose
					>
						{success}
					</Alert>
				</Container>
			)}

			{isValidArray(pageData?.modular_blocks) && (
				<div>
					{!showForm && (
						<>
							<Container className={styles.broContainer}>
								<Row>
									{
										// All blocks except additional content
										pageData.modular_blocks
											.filter((block) => block.image_aside == null && block.cards == null)
											.map((block, index) => {
												if (block.heading != null) {
													return (
														<Column
															id={block.heading.anchor_text || null}
															key={index}
															xs={12}
														>
															<h2 className={`${styles.sectionTitle} s2-h2__alt`}>{block.heading.heading}</h2>
														</Column>
													);
												}

												if (block.residential != null && isValidArray(block.residential?.brochure)) {
													return (
														<Column
															key={index}
															xs={12}
															className={styles.broSection}
														>
															<Row verticalGutters={true}>
																{block.residential.brochure.map((item, index) => (
																	<Column
																		key={index}
																		xs={12}
																		xl={6}
																	>
																		<BrochureCard
																			data={item}
																			mailMe={item.mail_me}
																			onMailMeClick={() => {
																				updateBrochureSelection(item);
																				setShowForm(true);
																			}}
																		/>
																	</Column>
																))}
															</Row>
														</Column>
													);
												}

												if (block.commercial_brochure != null && isValidArray(block.commercial_brochure?.commercial)) {
													return (
														<Column
															key={index}
															xs={12}
															className={styles.broSection}
														>
															<Row verticalGutters={true}>
																{block.commercial_brochure.commercial.map((item, index) => (
																	<Column
																		key={index}
																		xs={12}
																		xl={6}
																	>
																		<BrochureCard data={item} />
																	</Column>
																))}
															</Row>
														</Column>
													);
												}

												if (block.pro_brochure != null && isValidArray(block.pro_brochure?.pro_brochure)) {
													return (
														<Column
															key={index}
															xs={12}
															className={styles.broSection}
														>
															<Row verticalGutters={true}>
																{block.pro_brochure.pro_brochure.map((item, index) => (
																	<Column
																		key={index}
																		xs={12}
																		xl={6}
																	>
																		<BrochureCard data={item} />
																	</Column>
																))}
															</Row>
														</Column>
													);
												}
											})
									}
								</Row>
							</Container>

							{
								// Additional Content (items that need to be outside the container)
								pageData.modular_blocks
									.filter((block) => block.image_aside != null || block.cards != null)
									.map((block, index) => {
										if (block.cards != null) {
											return (
												<CardSection
													data={block.cards}
													key={index}
												/>
											);
										}

										if (block.image_aside != null) {
											return (
												<ImageBanner
													data={block.image_aside}
													isContained={false}
													key={index}
												/>
											);
										}
									})
							}
						</>
					)}

					{showForm && (
						<Container className={styles.broContainer}>
							<OrderForm
								brochure={brochures}
								showForm={(e) => setShowForm(e)}
								clearBrochureState={() => setBrochures({})}
								setSuccess={(e) => setSuccess(e)}
							/>
						</Container>
					)}
				</div>
			)}
		</Layout>
	);
};

Brochures.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default Brochures;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackBrochures.seo} />;

export const query = graphql`
	query ($uid: String!) {
		contentstackBrochures(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			hero {
				image {
					...Image
					image {
						gatsbyImageData(width: 1500, quality: 90)
					}
				}
				body
			}
			top_content {
				heading
				subheading
				top_image {
					...Image
					image {
						gatsbyImageData(quality: 90)
					}
				}
				ctas {
					...CTA
				}
			}
			modular_blocks {
				heading {
					anchor_text
					heading
				}
				residential {
					brochure {
						image: insert_image {
							...Image
							image {
								gatsbyImageData(width: 80, quality: 90)
							}
						}
						heading
						description
						ctas {
							...CTA
						}
						brochure_code
						mail_me
					}
				}
				commercial_brochure {
					commercial {
						image: insert_image {
							...Image
							image {
								gatsbyImageData(width: 80, quality: 90)
							}
						}
						heading
						description
						ctas {
							...CTA
						}
					}
				}
				pro_brochure {
					pro_brochure {
						image: insert_image {
							...Image
							image {
								gatsbyImageData(width: 80, quality: 90)
							}
						}
						heading
						description
						ctas {
							...CTA
						}
					}
				}
				cards {
					...CardSection
				}
				image_aside {
					...ImageAside
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
